/** Two-pane namespace picker panel.
 *
 * Left pane: scrollable list of unlocked namespace keys (filtered by the typed
 * text) plus a pinned "Free text" pseudo-entry. Right pane: help + examples for
 * the highlighted namespace. Kept dumb/controlled — real keyboard handling
 * lives in QuerySearchInput via `highlightedIndex`/`onHighlightChange`.
 */
import { Box, List, ListItemButton, Paper, Popper, Typography } from '@mui/material';

import type { NamespaceConfig } from './types';

/** One selectable row: either the free-text sentinel or a namespace key. */
export interface DropdownRow {
  /** null for the pinned "Free text" pseudo-entry. */
  key: string | null;
  config?: NamespaceConfig;
}

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  rows: DropdownRow[];
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onSelect: (row: DropdownRow) => void;
}

function summarise(config: NamespaceConfig): string {
  return `${config.valueType} · ${config.operators.join('/')}`;
}

export function NamespaceDropdown({
  anchorEl,
  open,
  rows,
  highlightedIndex,
  onHighlightChange,
  onSelect,
}: Props) {
  const highlighted = rows[highlightedIndex];
  return (
    <Popper
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
    >
      <Paper elevation={4} sx={{ mt: 0.5, display: 'flex', width: 520, maxWidth: '90vw' }}>
        {/* Left: namespace list */}
        <Box sx={{ width: '45%', maxHeight: 320, overflowY: 'auto' }}>
          <List role="listbox" dense disablePadding>
            {rows.map((row, i) => {
              const selected = i === highlightedIndex;
              return (
                <ListItemButton
                  key={row.key ?? '__freetext__'}
                  role="option"
                  aria-selected={selected}
                  selected={selected}
                  // onMouseDown (not onClick) so selection fires before the
                  // input's blur closes the panel.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(row);
                  }}
                  onMouseEnter={() => onHighlightChange(i)}
                  sx={{ display: 'block', py: 0.5 }}
                >
                  {row.key === null ? (
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      Free text
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {row.config?.label ?? row.key}
                      </Typography>
                      {row.config && (
                        <Typography variant="caption" color="text.disabled">
                          {summarise(row.config)}
                        </Typography>
                      )}
                    </>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* Right: help + examples */}
        <Box
          sx={{
            width: '55%',
            borderLeft: 1,
            borderColor: 'divider',
            p: 1.5,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {highlighted?.key === null || highlighted === undefined ? (
            <Typography variant="body2" color="text.secondary">
              Type freely to search across all fields, or pick a field on the left
              to narrow your query.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {highlighted.config?.help}
              </Typography>
              {highlighted.config?.examples.map((ex) => (
                <Typography
                  key={ex}
                  variant="caption"
                  component="div"
                  sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                >
                  {ex}
                </Typography>
              ))}
            </>
          )}
        </Box>
      </Paper>
    </Popper>
  );
}
